import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/contexts/ToastContext';
import { workoutsApi } from '@/lib/api';
import { formatDateISO } from '@/lib/utils';
import { ChevronLeftIcon } from '@/components/Icons';
import LoadingSpinner from '@/components/LoadingSpinner';

export default function NewWorkoutPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const today = formatDateISO();
  const [date, setDate] = useState(today);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate date is not in the future
    if (date > today) {
      showToast('Нельзя создать тренировку на будущую дату', 'error');
      return;
    }

    setLoading(true);

    const res = await workoutsApi.create(date);

    if (res.success && res.data) {
      // Invalidate workouts cache so the list shows the new workout
      queryClient.invalidateQueries({ queryKey: ['workouts'] });
      queryClient.invalidateQueries({ queryKey: ['weeklyLive'] });
      navigate(`/workouts/${res.data.id}`);
    } else {
      showToast(res.error || 'Не удалось создать тренировку', 'error');
      setLoading(false);
    }
  };

  return (
    <div className="page-container min-h-screen flex flex-col">
      {/* Header */}
      <header className="page-header flex items-center">
        <button
          onClick={() => navigate(-1)}
          className="p-2 -ml-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
        >
          <ChevronLeftIcon className="w-5 h-5" />
        </button>
      </header>

      {/* Centered content */}
      <div className="flex-1 flex flex-col items-center justify-center px-4 -mt-16">
        {/* Icon */}
        <div className="w-20 h-20 bg-primary-100 rounded-full flex items-center justify-center mb-6">
          <svg className="w-10 h-10 text-primary-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
          </svg>
        </div>

        {/* Title */}
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Новая тренировка</h1>
        <p className="text-gray-500 text-center mb-8">Выберите дату и начните тренировку</p>

        {/* Form */}
        <form onSubmit={handleSubmit} className="w-full max-w-sm space-y-6">
          <div className="card p-4">
            <label htmlFor="date" className="block text-sm font-medium text-gray-700 mb-2">
              Дата тренировки
            </label>
            <input
              id="date"
              type="date"
              value={date}
              max={today}
              onChange={(e) => setDate(e.target.value)}
              className="input text-center text-lg"
              required
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="btn-primary w-full btn-lg text-lg"
          >
            {loading ? (
              <LoadingSpinner size="sm" className="text-white" />
            ) : (
              <>
                <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                </svg>
                Начать тренировку
              </>
            )}
          </button>
        </form>
      </div>
    </div>
  );
}
